import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Plan {
  id: string;
  bezeichnung: string;
  upload_id?: number;
  active: boolean;
  is_active?: boolean;
  activated_at?: string;
  sektion_id: string;
  standort_id: string;
  client_id: string;
  anmerkungen?: string;
  created_at: string;
  updated_at: string;
  // Computed fields
  upload?: FileUpload;
  sektion?: Sektion;
  standort?: Standort;
  client?: Kunde;
}

export interface Sektion {
  id: string;
  name: string;
  beschreibung?: string;
  gebaeude_id: string;
  stockwerk?: number;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export interface Standort {
  id: string;
  name: string;
  nummer: number;
  status?: number;
  kunde_id?: string;
  bemerkung_struktur_anmerkung?: string;
  zugriffsrechte?: number;
  niederlassung_code?: string;
  bericht_sprache?: string;
  anmeldung_besuche?: string;
  bericht_versand?: string;
  besuchsbericht?: boolean;
  mit_auswertung_tabelle?: boolean;
  kontaktadressen?: number;
  herkunft?: string;
  anrede?: string;
  account_id?: number;
  created_at: string;
  updated_at: string;
}

export interface Kunde {
  id: string;
  uuid: string;
  nummer?: string;
  name?: string;
  status: boolean;
  niederlassung_code?: string;
  suchbegriff?: string;
  status_id?: number;
  techniker_projekthinweis?: string;
  startdatum?: string;
  enddatum?: string;
  anmeldung_besuche?: string;
  periode_aufgaben_vorschlagen?: boolean;
  ueberpruefung_erforderlich?: string;
  bemerkung_next_evaluation?: string;
  wiederkehrende_aufgaben_vorhanden?: boolean;
  datum_letzte_wiederkehrende_aufgabe?: string;
  techniker_id?: number;
  account_id?: number;
  created_at: string;
  updated_at: string;
}

export interface FileUpload {
  id: number;
  user_id?: number;
  uploadDate: string;
  filename?: string;
  original_filename: string;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  table_name?: string;
  record_id?: string;
  description?: string;
  tags?: any;
  is_public?: boolean;
  download_count?: number;
  last_downloaded?: string;
  checksum?: string;
  user_name?: string;
  mobile_id?: string;
  app_key?: string;
  created_at: string;
  updated_at: string;
  usage_count?: number;
  usage_details?: any;
  is_protected?: boolean;
  user_full_name?: string;
  standort_name?: string;
}

export interface PlanResponse {
  data: Plan[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get all plans with pagination and filtering
   */
  getPlans(page: number = 1, limit: number = 25, search?: string, sektionId?: string, standortId?: string, clientId?: string, active?: boolean): Observable<PlanResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }
    if (sektionId) {
      params = params.set('sektion_id', sektionId);
    }
    if (standortId) {
      params = params.set('standort_id', standortId);
    }
    if (clientId) {
      params = params.set('client_id', clientId);
    }
    if (active !== undefined) {
      params = params.set('active', active.toString());
    }

    return this.http.get<PlanResponse>(`${this.apiUrl}/plan`, { params });
  }

  /**
   * Get a single plan by ID
   */
  getPlan(id: string): Observable<Plan> {
    return this.http.get<Plan>(`${this.apiUrl}/plan/${id}`);
  }

  /**
   * Create a new plan
   */
  createPlan(plan: Partial<Plan>): Observable<Plan> {
    return this.http.post<Plan>(`${this.apiUrl}/plan`, plan);
  }

  /**
   * Update an existing plan
   */
  updatePlan(id: string, plan: Partial<Plan>): Observable<Plan> {
    return this.http.put<Plan>(`${this.apiUrl}/plan/${id}`, plan);
  }

  /**
   * Delete a plan
   */
  deletePlan(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/plan/${id}`);
  }

  /**
   * Get plans by sektion
   */
  getPlansBySektion(sektionId: string, page: number = 1, limit: number = 25): Observable<PlanResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<PlanResponse>(`${this.apiUrl}/plan/sektion/${sektionId}`, { params });
  }

  /**
   * Get plans by standort
   */
  getPlansByStandort(standortId: string, page: number = 1, limit: number = 25): Observable<PlanResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<PlanResponse>(`${this.apiUrl}/plan/standort/${standortId}`, { params });
  }

  /**
   * Get plans by client
   */
  getPlansByClient(clientId: string, page: number = 1, limit: number = 25): Observable<PlanResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<PlanResponse>(`${this.apiUrl}/plan/client/${clientId}`, { params });
  }

  /**
   * Generate a UUID for new plans
   */
  generateUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Activate a plan
   */
  activatePlan(planId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/plan/${planId}/activate`, {});
  }

  /**
   * Deactivate a plan
   */
  deactivatePlan(planId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/plan/${planId}/deactivate`, {});
  }

  /**
   * Get active plan for a standort
   */
  getActivePlan(standortId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/plan/standort/${standortId}/active`);
  }

  /**
   * Convenience wrapper: get all uploads attached to a plan (symmetry with mobile).
   * Used by the plan-draw component to find the -bg and -draw.json companions.
   */
  getPlanUploads(planId: string): Observable<FileUpload[]> {
    return this.http.get<{ data: FileUpload[] | { [key: string]: FileUpload[] } }>(`${this.apiUrl}/fileupload/plan/${planId}`).pipe(
      map(response => {
        const data = response?.data;
        if (Array.isArray(data)) {
          return data;
        }
        if (data && typeof data === 'object') {
          return Object.values(data).flat() as FileUpload[];
        }
        return [];
      })
    );
  }

  /**
   * Save indicator position on plan
   */
  saveIndicatorPosition(data: {
    plan_id: string;
    room_indikator_id: string;
    x_position: number;
    y_position: number;
    systemtyp: string;
    systemart: string;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/indikator-standort`, data);
  }

  /**
   * Get all indicator positions for a plan
   */
  getPlanIndicatorPositions(planId: string): Observable<Array<{
    room_indikator_id: string;
    x_position: number;
    y_position: number;
  }>> {
    return this.http.get<{ data: Array<any> }>(`${this.apiUrl}/indikator-standort/plan/${planId}`).pipe(
      map(response => {
        // Extract data array and map to expected format
        return (response.data || []).map((item: any) => ({
          room_indikator_id: item.room_indikator_id,
          x_position: item.x_position,
          y_position: item.y_position
        }));
      })
    );
  }

  /**
   * Save interactive plan image to disk
   */
  saveInteractivePlan(
    originalUploadId: number,
    imageDataUrl: string,
    format: 'png' | 'jpeg' = 'jpeg',
    planId?: string
  ): Observable<{result: number, data: FileUpload}> {
    // Convert data URL to base64 (handle both data:image/...;base64,... and plain base64)
    let base64Data: string;
    if (imageDataUrl.startsWith('data:')) {
      base64Data = imageDataUrl.split(',')[1];
    } else {
      base64Data = imageDataUrl;
    }
    
    const payload: any = {
      original_upload_id: originalUploadId,
      image_data: base64Data,
      format: format
    };
    
    if (planId) {
      payload.plan_id = planId;
    }
    
    return this.http.post<{result: number, data: FileUpload}>(
      `${this.apiUrl}/fileupload/save-interactive-plan`,
      payload
    );
  }

  /**
   * Export plan as PDF
   * @param planId Plan ID
   * @param planImageBase64 Base64 encoded plan image (null if using saved image)
   * @param useSavedImage Whether to prefer saved interactive plan image (default: true)
   */
  exportPlanAsPDF(planId: string, planImageBase64: string | null, useSavedImage: boolean = true): Observable<Blob> {
    // Get current user ID (simple approach - can be enhanced with auth service)
    const generatedBy = 'User'; // TODO: Get from auth service if available
    
    // Use POST if base64 data is provided and large (to avoid URL length limits)
    if (planImageBase64 && planImageBase64.length > 2000) {
      return this.http.post(
        `${this.apiUrl}/pdf/plan/${planId}`,
        {
          planImage: planImageBase64,
          useSavedImage: useSavedImage,
          generatedBy: generatedBy
        },
        {
          responseType: 'blob'
        }
      );
    }
    
    // Use GET for small images or when using saved image
    let params = new HttpParams()
      .set('useSavedImage', useSavedImage.toString())
      .set('generatedBy', generatedBy);
    
    // Only add planImage if provided (not using saved image)
    if (planImageBase64 && !useSavedImage) {
      params = params.set('planImage', planImageBase64);
    }
    
    return this.http.get(`${this.apiUrl}/pdf/plan/${planId}`, {
      params: params,
      responseType: 'blob'
    });
  }
}
